import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const cleanEmail = email.trim().toLowerCase();
    const cleanPw = password; // preserve inner spaces just in case
    if (!cleanEmail || !cleanPw) {
      setError("Enter your email and password to log in.");
      return;
    }
    setBusy(true);
    const res = await login(cleanEmail, cleanPw);
    setBusy(false);
    if (res.ok) {
      toast.success(`G'day ${res.user.name}!`);
      nav("/", { replace: true });
    } else {
      // Only append the case-sensitivity hint when the failure is credential-related
      // (not for network/server errors where the hint would be misleading).
      const msg = res.error || "Login failed.";
      const looksLikeAuth = /invalid|password|credential|email/i.test(msg);
      setError(looksLikeAuth
        ? `${msg} Passwords are case-sensitive — check for stray spaces or autocorrect changes.`
        : msg);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#f8f9fa] flex flex-col">
      <div
        className="h-56 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #1864ab 0%, #1971c2 100%)" }}
      >
        <div className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1635895882609-942f36e1db5d?crop=entropy&cs=srgb&fm=jpg&q=60&w=800')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            mixBlendMode: "overlay",
          }}
        />
        <div className="relative h-full max-w-[480px] mx-auto px-6 flex flex-col justify-end pb-6">
          <div className="text-5xl mb-2">🐎</div>
          <h1 className="font-display font-black text-white text-4xl leading-tight tracking-tight">
            HorseTips4fun
          </h1>
          <p className="text-white/85 text-sm font-medium mt-1">
            Mates. Horses. Bragging rights.
          </p>
        </div>
      </div>

      <div className="flex-1 max-w-[480px] w-full mx-auto px-6 pt-8 pb-12">
        <div className="bg-white border border-[#e9ecef] rounded-2xl p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]">
          <h2 className="font-display font-extrabold text-2xl text-[#212529]">
            Log in
          </h2>
          <p className="text-sm text-[#6c757d] mt-1">
            Admins & owner login here.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#6c757d] mb-2">
                Email
              </label>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border-2 border-[#e9ecef] rounded-xl px-4 py-3 focus:outline-none focus:border-[#1971c2] focus:ring-2 focus:ring-[#1971c2]/20 font-medium text-base transition-colors"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#6c757d] mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border-2 border-[#e9ecef] rounded-xl px-4 py-3 pr-12 focus:outline-none focus:border-[#1971c2] focus:ring-2 focus:ring-[#1971c2]/20 font-medium text-base transition-colors"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-lg flex items-center justify-center text-[#6c757d] hover:bg-[#f8f9fa]"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  data-testid="login-password-toggle"
                >
                  {showPw ? <EyeOff className="w-5 h-5" strokeWidth={2.5} /> : <Eye className="w-5 h-5" strokeWidth={2.5} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="rounded-xl bg-[#fdecec] border-2 border-[#c92a2a]/40 text-[#c92a2a] text-sm font-bold px-4 py-3 flex items-start gap-2" data-testid="login-error" role="alert">
                <span className="text-lg leading-none">⚠️</span>
                <div className="flex-1">
                  <div className="font-black">Login failed</div>
                  <div className="font-semibold text-[13px] mt-0.5">{error}</div>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full bg-[#1971c2] hover:bg-[#1864ab] text-white font-display font-extrabold text-base rounded-xl py-3.5 active:scale-95 transition-transform shadow-md disabled:opacity-60"
              data-testid="login-submit-button"
            >
              {busy ? "Logging in…" : "Log in"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-[#6c757d]">
              Have an invite?{" "}
              <span className="font-bold text-[#1971c2]">Paste your link</span> straight into the browser bar to join.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
